/**
 * QUIPWA — localStorage keys and settings helpers
 *
 * All localStorage access goes through this module.
 * API keys never leave the device and are never logged.
 *
 * Spec section 9
 */

import type { UserSettings } from '../../types/domain.ts';
import { useUserSettings } from '../settings/user-settings.ts';

// ─── KEYS ────────────────────────────────────────────────────────────────────

export const LS_KEYS = {
  GOOGLE_REFRESH_TOKEN:   'google_refresh_token',
  QUIPWA_ROOT_FOLDER_ID:  'QUIPWA_root_folder_id',
  USER_SETTINGS:          'user_settings',
  ONBOARDING_COMPLETED:   'onboarding_completed',
  // Drive state sync
  WF_STATE_FILE_ID:       'wf_state_file_id',   // Drive file ID of _state.db
  WF_STATE_MODIFIED:      'wf_state_modified',   // unix ts of last known Drive version
} as const;

// ─── USER SETTINGS ────────────────────────────────────────────────────────────

/**
 * Current study preferences. They are replicated now (lib/settings); this
 * accessor is kept so the scheduler and older call sites read one source.
 */
export function loadUserSettings(): UserSettings {
  return useUserSettings.getState().study;
}

/** @deprecated use `useUserSettings.getState().updateStudy` */
export function saveUserSettings(settings: UserSettings): void {
  void useUserSettings.getState().updateStudy(settings);
}



// ─── DRIVE ROOT ───────────────────────────────────────────────────────────────

export function getRootFolderId(): string | null {
  return localStorage.getItem(LS_KEYS.QUIPWA_ROOT_FOLDER_ID);
}

export function setRootFolderId(id: string): void {
  localStorage.setItem(LS_KEYS.QUIPWA_ROOT_FOLDER_ID, id);
}

// ─── DRIVE STATE DB SYNC ──────────────────────────────────────────────────────

/** Drive file ID of the user's _state.db — needed to update instead of create */
export function getStateFileId(): string | null {
  return localStorage.getItem(LS_KEYS.WF_STATE_FILE_ID);
}

export function setStateFileId(id: string): void {
  localStorage.setItem(LS_KEYS.WF_STATE_FILE_ID, id);
}

/** Unix timestamp of the last Drive version we synced (to avoid re-downloading) */
export function getStateModified(): number {
  return parseInt(localStorage.getItem(LS_KEYS.WF_STATE_MODIFIED) ?? '0', 10);
}

export function setStateModified(ts: number): void {
  localStorage.setItem(LS_KEYS.WF_STATE_MODIFIED, String(ts));
}

// ─── ONBOARDING ──────────────────────────────────────────────────────────────

export function isOnboardingCompleted(): boolean {
  return localStorage.getItem(LS_KEYS.ONBOARDING_COMPLETED) === 'true';
}

export function markOnboardingComplete(): void {
  localStorage.setItem(LS_KEYS.ONBOARDING_COMPLETED, 'true');
}

